/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Saturday, September 17th 2022, 9:41:50 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Stack from "@mui/material/Stack";
import { useFieldDependenciesAPI } from "form-builder/services/FormsAPI";
import { useFormBuilderStore, currentFormSelector } from "form-builder/stores";
import { APIListProgressWrapper, OSActionButton, useToggleState } from "ui-widgets";
import { EmptyFieldDependenciesCTA } from "./EmptyFieldDependenciesCTA";
import { FieldDependencyDetail } from "./FieldDependencyDetail";
import { FieldDependencyFormModal } from "./FieldDependencyForm";

export const FieldDependencies = () => {
  const {
    isFetchingDependencies,
    isDependencyFetchError,
    fieldDependencies,
    currentFormConfiguration,
    newOpen,
    handleOpenNew,
    handleCloseNew,
  } = useFieldDependencies();

  return (
    <APIListProgressWrapper
      showLoader={isFetchingDependencies}
      loaderType="skeleton"
      skeletonProps={{ height: 400, sx: { transform: "unset" } }}
      isAPIError={isDependencyFetchError}
      items={fieldDependencies}
      noDataMessage={<EmptyFieldDependenciesCTA form={currentFormConfiguration} />}
    >
      <Stack className="field-dependencies">
        <OSActionButton onClick={handleOpenNew} variant="text" size="large">
          Add New Field Dependency
        </OSActionButton>
        <FieldDependencyFormModal open={newOpen} handleClose={handleCloseNew} fieldDependencyId={null} />
        {fieldDependencies?.map((fieldDependency) => (
          <React.Fragment key={fieldDependency.id}>
            <FieldDependencyDetail fieldDependency={fieldDependency} />
          </React.Fragment>
        ))}
      </Stack>
    </APIListProgressWrapper>
  );
};

const useFieldDependencies = () => {
  const currentFormConfiguration = useFormBuilderStore(currentFormSelector);
  const addFieldDependency = useFormBuilderStore((state) => state.addFieldDependency);
  const fieldDependencies = useFormBuilderStore((state) =>
    state.fieldDependencies?.filter((fieldDependency) => fieldDependency.form === currentFormConfiguration?.id)
  );

  const { useList } = useFieldDependenciesAPI();
  const { makeRequest, isMakingAPIRequest, isAPIError } = useList();

  React.useEffect(() => {
    if (fieldDependencies?.length) return;
    if (currentFormConfiguration?.id) {
      makeRequest({
        apiQueryParams: { form: currentFormConfiguration?.id },
        setValidAPIResponse: (dependencyList) => dependencyList.forEach((dependency) => addFieldDependency(dependency)),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFormConfiguration?.id, makeRequest, addFieldDependency, fieldDependencies?.length]);

  const { handleClose: handleCloseNew, handleOpen: handleOpenNew, open: newOpen } = useToggleState();

  return {
    isFetchingDependencies: isMakingAPIRequest,
    isDependencyFetchError: isAPIError,
    fieldDependencies,
    currentFormConfiguration,
    newOpen,
    handleCloseNew,
    handleOpenNew,
  };
};
